// Groups store — reactive state for group membership, real-time subscriptions, and UID resolution.
// Groups are only available to non-anonymous users.

import { authState } from './auth.svelte.js';
import { getUserGroups, getGroupDetail, refreshProfile } from '../services/groups-api.js';
import {
  resolveDisplayNameFromMap,
  isUserInGroupList,
  getUserRoleInGroupList,
} from '../logic/groups-logic.js';

// ============================================================
// State
// ============================================================

export const groupsState = $state({
  /** @type {import('../types.js').UserGroup[]} */
  userGroups: [],
  /** @type {boolean} */
  loaded: false,
  /** @type {boolean} */
  loading: false,
  /** @type {Object.<string, string[]>} groupId → uid[] */
  memberUidsByGroup: {},
  /** @type {Object.<string, string>} uid → displayName */
  memberUidToName: {},
  /** @type {Object.<string, string|null>} uid → photoURL */
  memberUidToPhoto: {},
  /** @type {string|null} */
  activeGroupId: null,
  /** @type {Object|null} */
  activeGroup: null,
  /** @type {Array} */
  activeGroupMembers: [],
  /** @type {Array} */
  activeGroupInvites: [],
  /** @type {import('../types.js').Subgroup[]} */
  activeGroupSubgroups: [],
});

// ============================================================
// Group List
// ============================================================

/**
 * Load all groups the current user belongs to, plus their member lists for
 * UID resolution and wantlist highlighting.
 * No-op if user is anonymous or already loading.
 */
export async function loadUserGroups() {
  if (authState.user?.isAnonymous || !authState.user) return;
  if (groupsState.loading) return;

  groupsState.loading = true;
  try {
    // Fire-and-forget: push the current Firebase Auth profile (fresh from
    // Discord/Google OAuth) into the user's group_members rows so their
    // avatar shows up for everyone else. We don't await it — it's a backfill
    // for *other* viewers; this page doesn't show the current user's own
    // avatar in the card previews.
    refreshProfile({
      displayName: authState.user?.displayName ?? null,
      photoURL: authState.user?.photoURL ?? null,
    }).catch((err) => console.warn('[Groups] Profile refresh failed:', err));

    const groups = await getUserGroups();
    groupsState.userGroups = groups;

    // Fetch members for each group in parallel so we can resolve UIDs → names
    const detailsByGroup = await Promise.all(
      groups.map(async (group) => {
        try {
          const detail = await getGroupDetail(group.groupId);
          return { groupId: group.groupId, members: detail.members || [] };
        } catch {
          return { groupId: group.groupId, members: [] };
        }
      })
    );

    /** @type {Record<string, string[]>} */
    const uidsByGroup = {};
    /** @type {Record<string, string>} */
    const uidToName = {};
    /** @type {Record<string, string>} */
    const uidToPhoto = {};

    for (const { groupId, members } of detailsByGroup) {
      uidsByGroup[groupId] = members.map((m) => m.uid || m.id);
      for (const member of members) {
        const uid = member.uid || member.id;
        if (!uid) continue;
        // getGroupDetail maps snake_case → camelCase (displayName/photoURL).
        if (member.displayName) uidToName[uid] = member.displayName;
        if (member.photoURL) uidToPhoto[uid] = member.photoURL;
      }
    }

    groupsState.memberUidsByGroup = uidsByGroup;
    groupsState.memberUidToName = uidToName;
    groupsState.memberUidToPhoto = uidToPhoto;
    groupsState.loaded = true;
    console.log('[Groups] Loaded', groups.length, 'groups');
  } catch (err) {
    console.error('[Groups] Failed to load user groups:', err);
  } finally {
    groupsState.loading = false;
  }
}

/**
 * Refresh group list (e.g., after joining or creating a group).
 */
export async function refreshUserGroups() {
  groupsState.loaded = false;
  await loadUserGroups();
}

// ============================================================
// Active Group (real-time subscription)
// ============================================================

/**
 * Open a group and fetch its data. Call closeGroup() to reset state.
 * @param {string} groupId
 */
export async function openGroup(groupId) {
  // Close any previously open group first
  closeGroup();

  groupsState.activeGroupId = groupId;
  groupsState.activeGroup = null;
  groupsState.activeGroupMembers = [];
  groupsState.activeGroupInvites = [];
  groupsState.activeGroupSubgroups = [];

  // Fetch group + members + invites in one call
  await refreshActiveGroupData(groupId);
}

/**
 * Refresh members and invites for the currently open group.
 * Call this after role changes, removals, or invite operations.
 * @param {string} [groupId] Defaults to activeGroupId
 */
export async function refreshActiveGroupData(groupId = groupsState.activeGroupId) {
  if (!groupId) return;
  try {
    const { group, members, invites, subgroups } = await getGroupDetail(groupId);
    groupsState.activeGroup = group;
    groupsState.activeGroupMembers = members || [];
    groupsState.activeGroupInvites = invites || [];
    groupsState.activeGroupSubgroups = subgroups || [];
  } catch (err) {
    console.error('[Groups] Failed to refresh group data:', err);
  }
}

/**
 * Close the active group and clean up the real-time subscription.
 */
export function closeGroup() {
  groupsState.activeGroupId = null;
  groupsState.activeGroup = null;
  groupsState.activeGroupMembers = [];
  groupsState.activeGroupInvites = [];
  groupsState.activeGroupSubgroups = [];
}

// ============================================================
// UID / Name Helpers
// ============================================================

/**
 * Resolve a UID to a display name from the loaded member map.
 * Returns the uid itself as a fallback if no name is found.
 * @param {string} uid
 * @returns {string}
 */
export function resolveDisplayName(uid) {
  return resolveDisplayNameFromMap(groupsState.memberUidToName, uid);
}

/**
 * Check if the current user is a member of a specific group.
 * @param {string} groupId
 * @returns {boolean}
 */
export function isUserInGroup(groupId) {
  return isUserInGroupList(groupsState.userGroups, groupId);
}

/**
 * Get the current user's role in a specific group.
 * @param {string} groupId
 * @returns {import('../types.js').GroupRole | null} null when not a member
 */
export function getUserRoleInGroup(groupId) {
  return getUserRoleInGroupList(groupsState.userGroups, groupId);
}
